import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import {
  ApiResponse,
  CreateInvoiceRequest,
  InvoiceClient,
  InvoiceDetail,
  InvoiceIssuer,
  InvoiceListResponse,
  InvoicesQueryParams,
} from "../../types/invoice";
import { OcOrderOption } from "../../types/oc";

export const invoiceApi = createApi({
  reducerPath: "invoiceApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["InvoiceIssuers", "InvoiceClients", "InvoiceOcOrders", "Invoices"],
  endpoints: (builder) => ({
    // GET /api/invoices/issuers — emisoras (BeApolo / Ti Media Group)
    getInvoiceIssuers: builder.query<ApiResponse<InvoiceIssuer[]>, void>({
      query: () => "/invoices/issuers",
      providesTags: ["InvoiceIssuers"],
    }),

    // GET /api/invoices/clients — empresas cliente con su integración Holded
    getInvoiceClients: builder.query<ApiResponse<InvoiceClient[]>, void>({
      query: () => "/invoices/clients",
      providesTags: ["InvoiceClients"],
    }),

    // GET /api/invoices/oc-orders?clientId= — OCs de ese cliente para el selector de la
    // factura. El parámetro es el companyId de la opción de /clients, no el billingId.
    getInvoiceOcOrders: builder.query<ApiResponse<OcOrderOption[]>, string>({
      query: (clientId) =>
        `/invoices/oc-orders?clientId=${encodeURIComponent(clientId)}`,
      providesTags: ["InvoiceOcOrders"],
    }),

    // POST /api/invoices — crea la factura y la envía a Holded.
    // 201: creada + enviada (SENT). 502: creada en local pero Holded falló (ERROR);
    // el envelope viaja igualmente en err.data con el InvoiceDetail. 400: validación.
    createInvoice: builder.mutation<
      ApiResponse<InvoiceDetail>,
      CreateInvoiceRequest
    >({
      query: (body) => ({
        url: "/invoices",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Invoices"],
    }),

    // GET /api/invoices — histórico paginado con filtros
    getInvoices: builder.query<
      ApiResponse<InvoiceListResponse>,
      InvoicesQueryParams | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params) {
          const {
            page,
            pageSize,
            clientId,
            issuerCompanyId,
            ocOrderId,
            holdedStatus,
            startDate,
            endDate,
            search: q,
          } = params;
          if (page != null) search.append("page", String(page));
          if (pageSize != null) search.append("pageSize", String(pageSize));
          if (clientId) search.append("clientId", clientId);
          if (issuerCompanyId) search.append("issuerCompanyId", issuerCompanyId);
          if (ocOrderId) search.append("ocOrderId", ocOrderId);
          if (holdedStatus) search.append("holdedStatus", holdedStatus);
          if (startDate) search.append("startDate", startDate);
          if (endDate) search.append("endDate", endDate);
          if (q) search.append("search", q);
        }
        const qs = search.toString();
        return `/invoices${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["Invoices"],
    }),

    // GET /api/invoices/{id} — detalle completo con snapshots
    getInvoiceById: builder.query<ApiResponse<InvoiceDetail>, string>({
      query: (id) => `/invoices/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Invoices", id }],
    }),

    // POST /api/invoices/{id}/retry — reenvía a Holded.
    // 200: data actualizado (SENT, o sin reenviar si ya estaba SENT). 502: sigue ERROR.
    retryInvoice: builder.mutation<ApiResponse<InvoiceDetail>, string>({
      query: (id) => ({
        url: `/invoices/${id}/retry`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => [
        "Invoices",
        { type: "Invoices", id },
      ],
    }),

    // DELETE /api/invoices/{id} — borra solo el histórico local (no Holded; solo ADMIN)
    deleteInvoice: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: `/invoices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Invoices"],
    }),
  }),
});

export const {
  useGetInvoiceIssuersQuery,
  useGetInvoiceClientsQuery,
  useGetInvoiceOcOrdersQuery,
  useCreateInvoiceMutation,
  useGetInvoicesQuery,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useRetryInvoiceMutation,
  useDeleteInvoiceMutation,
} = invoiceApi;
